import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '@env/environment';
import { Insured } from './insured.interface';
import { Observable, ReplaySubject, of } from 'rxjs';
import { catchError, tap, map } from 'rxjs/operators';
import { RequestMetadata } from '../../common.interface';
import { Page } from '@core/types/production/dto';

@Injectable({ providedIn: 'root' })
export class InsuredService {

  baseUrl = environment.customer_url + '/insureds';
  private _insured = new ReplaySubject<Insured>(1);
  private _insureds = new ReplaySubject<Insured[]>(1);
  private _metadata = new ReplaySubject<RequestMetadata>(1);

  set insured(value: Insured) {
    this._insured.next(value);
  }

  get insured$() {
    return this._insured.asObservable();
  }

  set insureds(value: Insured[]) {
    this._insureds.next(value);
  }

  get insureds$() {
    return this._insureds.asObservable();
  }

  set metadata(value: RequestMetadata) {
    this._metadata.next(value);
  }

  get metadata$() {
    return this._metadata.asObservable();
  }

  constructor(private _httpClient: HttpClient) { }

  /**
   * POST /customer/app/insureds. Les erreurs HTTP remontent au call-site (le
   * swallow precedent retournait `{} as Insured` ce qui masquait les 400/422
   * et faisait soumettre des polices avec `insuredUuid` vide cote olive_core).
   */
  create(insured: Insured): Observable<Insured> {
    return this._httpClient.post<Insured>(`${this.baseUrl}`, insured).pipe(
      map(res => new Insured(res)),
      tap((res) => this.insured = res)
    );
  }

  get(id: string): Observable<Insured> {
    return this._httpClient.get<Insured>(`${this.baseUrl}/${id}`).pipe(
      map(res => new Insured(res)),
      tap((res) => this.insured = res),
      catchError(() => of({} as Insured))
    );
  }

  update(uuid: string, data: any): Observable<Insured> {
    return this._httpClient.put<Insured>(`${this.baseUrl}/${uuid}`, data).pipe(
      map(res => new Insured(res)),
      tap(res => this.insured = res),
      catchError(() => of({} as Insured))
    );
  }

  delete(uuid: string): Observable<void> {
    return this._httpClient.delete<void>(`${this.baseUrl}/${uuid}`);
  }

  /**
   * olive_customer renvoie {@code Page<InsuredResponse>} (Spring) sur
   * {@code GET /app/insureds} — desencapsuler {@code page.content}.
   * Cap a 200 par appel ; au-dela, l'ecran liste devra paginer cote serveur.
   */
  getAll(): Observable<Insured[]> {
    const params = new HttpParams().set('page', '0').set('size', '200');
    return this._httpClient.get<Page<Insured> | Insured[]>(this.baseUrl, { params }).pipe(
      map((response) => {
        const items = Array.isArray(response) ? response : (response?.content ?? []);
        return items.map(i => new Insured(i));
      }),
      tap((response: Insured[]) => {
        this.insureds = response;
        return response;
      }),
      catchError(() => of([] as Insured[]))
    );
  }

  /**
   * Recherche des assurés avec pagination et filtrage
   * @param filters - Filtres de recherche (page, size, q pour rechercher par nom)
   * @returns Page paginée d'Insured ou fallback avec filtrage côté client
   */
  getStats(): Observable<{ total: number; companies: number; individuals: number }> {
    return this._httpClient.get<{ total: number; companies: number; individuals: number }>(`${this.baseUrl}/stats`).pipe(
      catchError(() => of({ total: 0, companies: 0, individuals: 0 }))
    );
  }

  searchInsureds(filters: {
    page?: number;
    size?: number;
    q?: string;
  }): Observable<Page<Insured>> {
    const page = filters.page ?? 0;
    const size = filters.size ?? 50;

    // Tentative avec endpoint paginé (si backend le supporte)
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    if (filters.q) {
      // olive_customer expose le param sous le nom court 'q' (cf. InsuredAppController.search)
      params = params.set('q', filters.q);
    }

    return this._httpClient.get<Page<Insured>>(`${this.baseUrl}/search`, { params }).pipe(
      map(page => {
        page.content = page.content.map(i => new Insured(i));
        return page;
      }),
      catchError(() => {
        // Fallback: Si l'endpoint /search n'existe pas, utiliser getAll() et filtrer côté client
        return this.getAll().pipe(
          map(insureds => {
            let filtered = insureds;

            // Filtrer par query si fourni
            if (filters.q) {
              const lowerQuery = filters.q.toLowerCase();
              filtered = insureds.filter(i =>
                i.firstName?.toLowerCase().includes(lowerQuery) ||
                i.lastName?.toLowerCase().includes(lowerQuery) ||
                i.email?.toLowerCase().includes(lowerQuery)
              );
            }

            // Pagination côté client
            const start = page * size;
            const end = start + size;
            const paginatedContent = filtered.slice(start, end);

            return {
              content: paginatedContent,
              totalElements: filtered.length,
              totalPages: Math.ceil(filtered.length / size),
              number: page,
              size: size,
              pageable: {} as any,
              first: page === 0,
              last: end >= filtered.length,
              empty: paginatedContent.length === 0
            } as Page<Insured>;
          })
        );
      })
    );
  }
}
